import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../constants/routes";
import {
  AlertComponent,
  BreadcrumbComponent,
  Button,
  ConfirmationPopup,
  PaginationComponent,
} from "../../../../common/components";
import MUIBoxTable from "../../../../common/components/mui-table/mui-box-table";
import CustomActionsDropdown from "./actions-dropdown";
import CreateCustomPlan from "./create-custom-plan/create-custom-plan";
import AssignPartner from "./assign-partner-dialog/assign-partner";
import ViewPlanDetails from "../crm-paid-plans/view-plan-details";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { getSubscriptionPlans } from "../../../../api/tenets";
import {
  archiveCustomCRMPlan,
  toggleCRMPlanStatus,
} from "../../../../api/crm-plans";
import { convertUTCToLocal } from "../../../../utils/utils";
import {
  initialValues,
  PlanValues,
  transformKeysToCamelCase,
  validationSchema,
} from "../crm-plans-utils";
import { Form, Formik } from "formik";
import "./crm-custom-plans.scss";
import { AlertType } from "../../../../utils/util-types";
import { ALERT_TYPE } from "../../../../utils/constants";

const CustomPlans = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);

  const [createCustomPlanOpen, setCreateCustomPlanOpen] = useState(false);
  const [editPlanOpen, setEditPlanOpen] = useState(false);
  const [selectedEditPlan, setSelectedEditPlan] = useState<any>(null);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);

  const [assignPartnerOpen, setAssignPartnerOpen] = useState(false);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const [statusToggleConfirmPopupOpen, setStatusToggleConfirmPopupOpen] =
    useState(false);
  const [statusTogglePlan, setStatusTogglePlan] = useState<{
    id: number;
    name: string;
    targetStatus: boolean;
  } | null>(null);
  const [selectedArchivePlan, setSelectedArchivePlan] = useState<{
    id: number;
    name: string;
    assignedPartnerLength: number;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<AlertType>(
    ALERT_TYPE.SUCCESS
  );

  const [planInitialValues, setPlanInitialValues] =
    useState<typeof initialValues>(initialValues);

  const [isViewPlan, setIsViewPlan] = useState(false);

  const listBreadcrumb = [
    { label: "CRM Plans", link: "#", handleClick: () => {} },
    { label: "Custom Plans", link: routes.CRM_CUSTOM_PLANS },
  ];

  const createBreadcrumb = [
    { label: "CRM Plans", link: "#", handleClick: () => {} },
    {
      label: "Custom Plans",
      link: routes.CRM_CUSTOM_PLANS,
      handleClick: () => setCreateCustomPlanOpen(false),
    },
    { label: "Create Custom Plan", link: "" },
  ];

  const viewOrEditBreadCrumb = [
    { label: "CRM Plans", link: "#", handleClick: () => {} },
    {
      label: "Custom Plans",
      link: routes.CRM_CUSTOM_PLANS,
      handleClick: () => {
        setEditPlanOpen(false);
        setIsViewPlan(false);
      },
    },
    {
      label: selectedEditPlan
        ? isViewPlan
          ? `View ${selectedEditPlan.planName}`
          : `Edit ${selectedEditPlan.planName}`
        : "Custom Plan",
      link: "",
    },
  ];

  const headers = [
    {
      key: "actions",
      label: "",
      disableTooltip: true,
      render: (row: any) => (
        <div onClick={(e) => e.stopPropagation()}>
          <CustomActionsDropdown
            row={row}
            handleEdit={() => {
              setEditPlanOpen(true);
              setSelectedEditPlan(row);
            }}
            handleDuplicate={() => handleDuplicatePlan(row)}
            setArchive={() =>
              handleArchive(row.id, row.planName, row.assignedPartnerLength)
            }
            setAssignPartner={() => handleAssignPartner(row)}
            handleStatusToggle={() => {
              setStatusTogglePlan({
                id: row.id,
                name: row.planName,
                targetStatus: row.status === "Active" ? false : true,
              });
              setStatusToggleConfirmPopupOpen(true);
            }}
          />
        </div>
      ),
    },
    { key: "planName", label: "Plan Name" },
    {
      key: "monthlyPrice",
      label: "Monthly Price",
      render: (row: any) => `$${row.monthlyPrice}`,
    },
    {
      key: "annualPrice",
      label: "Annual Price",
      render: (row: any) => `$${row.annualPrice}`,
    },
    {
      key: "assignedPartner",
      label: "Assigned Partner",
      disableTooltip: true,
      render: (row: any) => {
        const subs = row.tenantSubscriptions || [];
        if (!subs.length) return <span>-</span>;
        return (
          <span>
            {subs.map((ts: any, idx: number) => (
              <div key={ts?.tenant?.id ?? idx}>
                <span
                  className="navigate-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(routes.PARTNERDETAILS, {
                      state: {
                        partnerDetails: ts.tenant,
                        defaultTabIndex: 1,
                      },
                    });
                  }}
                  style={{ cursor: "pointer", color: "#1976d2" }}
                >
                  {ts?.tenant?.company_name || "-"}
                </span>
                {idx < subs.length - 1 ? ", " : ""}
              </div>
            ))}
          </span>
        );
      },
    },
    { key: "createdBy", label: "Created By" },
    { key: "createdAt", label: "Created Date" },
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={
            row.status === "Active" ? "status-active" : "status-inactive"
          }
        >
          {row.status === "Active" ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  const getSubscriptions = async (page: number = 1) => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const filters = { limit: pageSize, offset, planCategory: "custom" };
      const response = await getSubscriptionPlans(filters);

      setTotalRecords(response?.data?.data?.totalPlansCount || 0);

      const camelCasedData = transformKeysToCamelCase(
        response?.data?.data.plans || []
      );
      const formattedData = camelCasedData.map((plan: any) => ({
        ...plan,
        tenantSubscriptions: plan.tenantSubscriptions,
        assignedPartner:
          plan.tenantSubscriptions
            .map((ts: any) => ts.tenant.company_name)
            .join(", ") || "-",
        assignedPartnerLength: plan.tenantSubscriptions.length,
        createdBy:
          [plan.createdBy?.first_name, plan.createdBy?.last_name]
            .filter(Boolean)
            .join(" ") || "-",
        createdAt: convertUTCToLocal(plan.createdAt),
        status: plan.active ? "Active" : "Inactive",
      }));

      setData(formattedData);
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchiveAPI = async (planId: number) => {
    try {
      setIsLoading(true);
      await archiveCustomCRMPlan(planId);
      setAlertMessage("Plan archived successfully.");
      setAlertType("success");
      setAlertOpen(true);
      getSubscriptions(currentPage);
    } catch (error: any) {
      console.error("Error archiving plan:", error);
      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to archive the plan. Please try again."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setIsArchivePopupOpen(false);
    }
  };

  const confirmStatusToggle = async () => {
    if (!statusTogglePlan) return;
    const { id, name, targetStatus } = statusTogglePlan;
    setIsLoading(true);
    try {
      await toggleCRMPlanStatus(id, { status: targetStatus });
      setAlertMessage(
        `Plan "${name}" ${targetStatus ? "activated" : "marked as inactive"} successfully.`
      );
      setAlertType("success");
      setAlertOpen(true);
      await getSubscriptions(currentPage);
    } catch (error) {
      console.error("Error toggling plan status:", error);
      setAlertMessage("Failed to change plan status. Please try again.");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setStatusToggleConfirmPopupOpen(false);
      setStatusTogglePlan(null);
    }
  };

  const handlePageChange = (_event: any, value: number) =>
    setCurrentPage(value);

  const handleArchive = (
    planId: number,
    planName: string,
    assignedPartnerLength: number
  ) => {
    setSelectedArchivePlan({
      id: planId,
      name: planName,
      assignedPartnerLength,
    });
    setIsArchivePopupOpen(true);
  };

  const handleDuplicatePlan = (row: Partial<PlanValues>) => {
    const duplicatePlan: PlanValues = {
      ...initialValues,
      ...Object.fromEntries(
        Object.entries(row).filter(
          ([key, value]) => key in initialValues && value !== undefined
        )
      ),
    };
    duplicatePlan.supportLevel = duplicatePlan.supportLevel.map((s) => ({
      ...s,
    }));
    setPlanInitialValues(duplicatePlan);
    setCreateCustomPlanOpen(true);
  };

  const handleAssignPartner = (plan: any) => {
    setSelectedPlan(plan);
    setAssignPartnerOpen(true);
  };

  const fetchPlans = () => {
    if (currentPage === 1) getSubscriptions(currentPage);
    else setCurrentPage(1);
  };

  const handlePlanUpdate = (updatedPlan: any) => {
    setData((prev) =>
      prev.map((p) => (p.id === updatedPlan.id ? { ...p, ...updatedPlan } : p))
    );
    setSelectedEditPlan(updatedPlan);
  };

  useEffect(() => {
    getSubscriptions(currentPage);
  }, [currentPage]);

  useSetLoading(isLoading);

  const EditCustomPlan: React.FC<{ plan: any; handlePlanUpdate: any }> = ({
    plan,
    handlePlanUpdate,
  }) => (
    <Formik
      initialValues={plan}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnBlur
      onSubmit={async () => {}}
    >
      {() => (
        <Form>
          <ViewPlanDetails
            title={plan?.planName}
            plans={plan}
            setPlans={setData}
            allPlans={data}
            onCustomPlanUpdate={handlePlanUpdate}
          />
        </Form>
      )}
    </Formik>
  );

  return (
    <div className="crm-custom-plans-container">
      <BreadcrumbComponent
        breadcrumbLink={
          createCustomPlanOpen
            ? createBreadcrumb
            : editPlanOpen || isViewPlan
              ? viewOrEditBreadCrumb
              : listBreadcrumb
        }
      />

      {createCustomPlanOpen ? (
        <CreateCustomPlan
          initialValues={planInitialValues}
          setIsLoading={setIsLoading}
          setAlertOpen={setAlertOpen}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          setCreateCustomPlanOpen={setCreateCustomPlanOpen}
          fetchPlans={fetchPlans}
        />
      ) : editPlanOpen || isViewPlan ? (
        <EditCustomPlan
          plan={selectedEditPlan}
          handlePlanUpdate={handlePlanUpdate}
        />
      ) : (
        <div className="custom-plans-content">
          <div className="create-custom-plan-btn">
            <Button
              onClick={() => {
                setCreateCustomPlanOpen(true);
                setPlanInitialValues(initialValues);
              }}
              label="Create Custom Plan"
            />
          </div>

          <div className="custom-plans-table">
            <MUIBoxTable
              columns={headers}
              data={data}
              noResultText="No custom plans found. Please create a custom plan to view the details."
              onRowClick={(rowData: any) => {
                setIsViewPlan(true);
                setSelectedEditPlan(rowData);
              }}
            />
          </div>

          {totalRecords > 0 && (
            <div className="custom-plans-pagination">
              <PaginationComponent
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={handlePageChange}
              />
            </div>
          )}

          <ConfirmationPopup
            open={isArchivePopupOpen}
            onCancel={() => setIsArchivePopupOpen(false)}
            onOkay={() =>
              selectedArchivePlan && handleArchiveAPI(selectedArchivePlan.id)
            }
            title="Archive Plan"
            note={`Are you sure you want to archive the plan "${selectedArchivePlan?.name}"`}
            buttonText="Archive"
          />

          {statusToggleConfirmPopupOpen && statusTogglePlan && (
            <ConfirmationPopup
              open
              onCancel={() => {
                setStatusToggleConfirmPopupOpen(false);
                setStatusTogglePlan(null);
              }}
              onOkay={confirmStatusToggle}
              title="Change Plan Status"
              note={`Are you sure you want to ${
                statusTogglePlan?.targetStatus ? "activate" : "inactivate"
              } this plan "${statusTogglePlan?.name ?? ""}"?`}
              buttonText={
                statusTogglePlan?.targetStatus ? "Activate" : "Inactivate"
              }
            />
          )}
          {assignPartnerOpen && selectedPlan && (
            <AssignPartner
              isOpen={assignPartnerOpen}
              setAssignPartnerPopup={setAssignPartnerOpen}
              selectedPlan={selectedPlan}
              setIsLoading={setIsLoading}
              fetchPlans={fetchPlans}
              setAlertOpen={setAlertOpen}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}
            />
          )}
        </div>
      )}

      <AlertComponent
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        type={alertType}
        message={alertMessage}
      />
    </div>
  );
};

export default CustomPlans;
